/**
 * Heading normalisation.
 *
 * Source `heading` blocks carry the tag WPBakery happened to emit, and the
 * `html` blocks carry whatever the editor typed. Neither guarantees one <h1>
 * per page or an unbroken level order. This module computes, per page, the
 * semantic level every heading should render at while leaving every word of
 * the copy untouched. The original level survives as a class so the design
 * layer can keep the visual size (`hp-h2`, `hp-h4`, ...).
 *
 * The result is a pure lookup keyed by the block object, so rendering order
 * and React's double-invoked renders cannot shift the assignment.
 */
import type { Block, HeadingBlockData } from './content';

const SINGLE_WRAPPER = /^<(h[1-6]|p|div)(\s[^>]*)?>([\s\S]*)<\/\1>$/i;
const BLOCK_LEVEL = /<(h[1-6]|p|div|ul|ol|table|blockquote|figure)\b/i;

export type HeadingShape =
  /** A single wrapping element whose inner content is inline-only. */
  | { kind: 'single'; tag: string; inner: string }
  /** No markup at all. */
  | { kind: 'plain'; tag: string; inner: string }
  /** Inline markup only, no wrapper. */
  | { kind: 'inline'; tag: string; inner: string }
  /** Several block-level elements: render as rich text, not as one heading. */
  | { kind: 'mixed'; inner: string };

/** Work out what a `heading` block actually contains. */
export function headingShape(block: HeadingBlockData): HeadingShape {
  const raw = (block.html ?? '').trim();
  const sourceTag = (block.tag ?? 'p').toLowerCase();
  const single = SINGLE_WRAPPER.exec(raw);
  if (single && !BLOCK_LEVEL.test(single[3])) {
    const wrapper = single[1].toLowerCase();
    // A heading tag in the markup wins; otherwise keep the block's own tag.
    const tag = /^h[1-6]$/.test(wrapper) ? wrapper : sourceTag;
    return { kind: 'single', tag, inner: single[3] };
  }
  if (BLOCK_LEVEL.test(raw)) return { kind: 'mixed', inner: raw };
  if (/</.test(raw)) return { kind: 'inline', tag: sourceTag, inner: raw };
  return { kind: 'plain', tag: sourceTag, inner: raw };
}

/** The source level a heading shape contributes, or null if it is not one. */
export function levelOfShape(shape: HeadingShape): number | null {
  if (shape.kind === 'mixed') return null;
  const m = /^h([1-6])$/.exec(shape.tag);
  return m ? Number(m[1]) : null;
}

/** How many headings an `html` string contributes. */
export function htmlHeadingCount(html: string): number {
  return [...html.matchAll(/<h[1-6]\b/gi)].length;
}

type Slot = { block: Block; src: number };

function collectSlots(blocks: Block[], out: Slot[]): Slot[] {
  for (const block of blocks) {
    switch (block.type) {
      case 'heading': {
        const shape = headingShape(block);
        if (shape.kind === 'mixed') {
          for (const m of shape.inner.matchAll(/<h([1-6])\b/gi)) {
            out.push({ block, src: Number(m[1]) });
          }
        } else {
          const lvl = levelOfShape(shape);
          if (lvl) out.push({ block, src: lvl });
        }
        break;
      }
      case 'section_title':
        out.push({ block, src: 2 });
        break;
      case 'accordion_item':
        out.push({ block, src: 3 });
        collectSlots(block.body ?? [], out);
        break;
      case 'html':
        for (const m of block.html.matchAll(/<h([1-6])\b/gi)) {
          out.push({ block, src: Number(m[1]) });
        }
        break;
      case 'portfolio_listing':
      case 'blog_listing':
        // The listing emits one heading per card; they all share a level.
        out.push({ block, src: 3 });
        break;
      default:
        break;
    }
  }
  return out;
}

/**
 * Headings that come from the copy itself. Card headings inside a listing do
 * not count: a page whose only heading is a listing still needs its title as
 * the <h1>.
 */
export function countHeadingSlots(blocks: Block[]): number {
  return collectSlots(blocks, []).filter(
    (s) => s.block.type !== 'portfolio_listing' && s.block.type !== 'blog_listing',
  ).length;
}

/**
 * Map source levels onto output levels so that the first slot is `startLevel`,
 * no level is ever skipped, and no second <h1> is produced.
 */
function assignLevels(sequence: number[], startLevel: number): number[] {
  const stack: { src: number; out: number }[] = [];
  const result: number[] = [];
  let first = true;
  for (const src of sequence) {
    while (stack.length && stack[stack.length - 1].src >= src) stack.pop();
    const floor = first ? startLevel : Math.max(startLevel, 2);
    const out = stack.length === 0 ? floor : Math.min(stack[stack.length - 1].out + 1, 6);
    stack.push({ src, out });
    result.push(out);
    first = false;
  }
  return result;
}

/**
 * Levels for every heading in a block tree, keyed by the block that owns them.
 * A block with several headings (a rich `html` block) gets them in order.
 */
export type HeadingLevels = {
  get(block: Block): number[];
  /** Level for a block that owns exactly one heading. */
  one(block: Block, fallback: number): number;
};

export function resolveHeadingLevels(blocks: Block[], startLevel: number): HeadingLevels {
  const slots = collectSlots(blocks, []);
  const levels = assignLevels(
    slots.map((s) => s.src),
    startLevel,
  );
  const map = new Map<Block, number[]>();
  slots.forEach((slot, i) => {
    const list = map.get(slot.block);
    if (list) list.push(levels[i]);
    else map.set(slot.block, [levels[i]]);
  });
  return {
    get(block) {
      return map.get(block) ?? [];
    },
    one(block, fallback) {
      return map.get(block)?.[0] ?? fallback;
    },
  };
}

/**
 * Rewrite the heading levels inside an html string. Only tag names and the
 * class attribute change; the text between the tags is untouched.
 */
export function applyHeadingLevels(html: string, levels: number[]): string {
  if (levels.length === 0) return html;
  let index = 0;
  const open: number[] = [];
  return html.replace(
    /<(\/?)h([1-6])((?:"[^"]*"|'[^']*'|[^>])*)>/gi,
    (match, slash: string, digit: string, attrs: string) => {
      const source = Number(digit);
      if (slash) {
        const closing = open.pop();
        return closing ? `</h${closing}>` : match;
      }
      const target = levels[index] ?? source;
      index += 1;
      open.push(target);
      const existing = /\sclass\s*=\s*"([^"]*)"/i.exec(attrs);
      const cls = `hp-h${source}${existing ? ' ' + existing[1] : ''}`;
      const rest = existing ? attrs.replace(existing[0], '') : attrs;
      return `<h${target} class="${cls}"${rest}>`;
    },
  );
}
